import { ScreenerQuestionAnwerResponse } from "@/services";
import dayjs from "dayjs";
import { HOME_HEALTH_SCREENER_TYPES, YES_NO } from "./assessment-constant";
import { FREE_TEXT_SCREENERS } from "./assessment-screener-constant";
import {
  AnswerRow,
  FreeTextAudience,
  FreeTextQuestionDef,
  FreeTextScreenerDef,
  FreeTextValues,
  QuestionValue,
  ScreenerQuestion,
} from "./assessment-types";

// Each answered question is saved as one row: `answer` holds the ticked
// options joined by ", " (or the typed value), `answerString` the note. Rows
// saved by the old format arrive as answer "Free text" with the whole string in
// `answerString`, and are still read back.

const FREE_TEXT_ANSWER = "Free text";
const ANSWER_SEPARATOR = ", ";
const NOTE_SEPARATOR = " - ";

const CONFIRMED = "Yes";

// Answers by question id, for the showIf rules.
export const answersById = (
  def: FreeTextScreenerDef,
  questions: ScreenerQuestion[],
  values: FreeTextValues
) => {
  const byId: Record<number, string[]> = {};
  questionsFor(def, questions).forEach(({ question, q }) => {
    byId[q.questionId] = values[question.uuid!]?.answers ?? [];
  });
  return byId;
};

export const isVisible = (
  q: FreeTextQuestionDef,
  byId: Record<number, string[]>
) =>
  (q.showIf ?? []).every((rule) =>
    (byId[rule.questionId] ?? []).includes(rule.equals)
  );

// The reason a visible question cannot be submitted yet, if any.
export const questionIssue = (
  q: FreeTextQuestionDef,
  title: string,
  value?: QuestionValue
) => {
  const answer = value?.answers[0];
  if (q.required && !answer) return `${title} is required`;
  if (q.kind === "date" && answer) {
    const picked = dayjs(answer);
    const today = dayjs().startOf("day");
    if (q.dateRule === "notFuture" && picked.isAfter(today, "day"))
      return `${title} cannot be in the future`;
    if (q.dateRule === "notPast" && picked.isBefore(today, "day"))
      return `${title} must be today or later`;
  }
  return "";
};

// Types arrive as "OBESITY_SCREENING", "OBESITY SCREENING" or "OBESITY_SCREENING (Obesity Screening)".
const normaliseType = (type?: string | null) =>
  (type ?? "")
    .replace(/\s*\(.*\)\s*$/, "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");

export const getFreeTextScreener = (type?: string | null) =>
  FREE_TEXT_SCREENERS[normaliseType(type)];

// Questions of the screener paired with their definition, optionally for one audience.
const normaliseText = (text?: string) =>
  (text ?? "")
    .trim()
    .replace(/\s+/g, " ")
    .replace(/[.:]+$/, "")
    .toLowerCase();

/** The API question a definition entry describes: by text when the entry names one, else by seeded id. */
const findQuestion = (q: FreeTextQuestionDef, questions: ScreenerQuestion[]) =>
  q.questionText
    ? questions.find(
        (it) => normaliseText(it.question) === normaliseText(q.questionText)
      )
    : questions.find((it) => Number(it.questionId) === q.questionId);

// Questions of the screener paired with their definition, in definition order,
// optionally for one audience.
export const questionsFor = (
  def: FreeTextScreenerDef,
  questions: ScreenerQuestion[],
  audience?: FreeTextAudience
) =>
  def.questions.flatMap((q) => {
    const question = findQuestion(q, questions);
    return question?.uuid && (!audience || q.audience === audience)
      ? [{ question, q }]
      : [];
  });

// A saved row as it reads: the typed text of a free-text row, else the option label.
export const screenerAnswerText = (row: {
  answer?: string | null;
  answerString?: string | null;
}) =>
  String(row.answer ?? "") === FREE_TEXT_ANSWER
    ? (row.answerString ?? "")
    : [row.answer, row.answerString].filter(Boolean).join(NOTE_SEPARATOR);

export const toAnswerRow = (
  question: ScreenerQuestion,
  { answers, notes }: QuestionValue
): AnswerRow | undefined => {
  const answer = answers.join(ANSWER_SEPARATOR);
  const answerString = notes.trim();
  if (!answer && !answerString) return undefined;
  return {
    uuid: question.uuid,
    question: question.question,
    answer,
    ...(answerString && { answerString }),
  };
};

// Option labels compared without case, spacing or punctuation, so a saved
// "Mostly take-out/restaurant" still ticks "Mostly take out restaurant".
const optionKey = (label: string) =>
  label.toLowerCase().replace(/[^a-z0-9]/g, "");

const matchOption = (
  q: FreeTextQuestionDef,
  part: string
): string | undefined =>
  q.options?.find((option) => optionKey(option) === optionKey(part));

// The saved string back to ticks and note: the part before " - " is split into
// options (or the number), anything unrecognised goes to the note.
export const fromAnswerString = (
  q: FreeTextQuestionDef,
  text: string
): QuestionValue => {
  const value: QuestionValue = { answers: [], notes: "" };
  if (!text || text === "Unknown") return value;
  const split = text.indexOf(NOTE_SEPARATOR);
  const head = split < 0 ? text : text.slice(0, split);
  const notes: string[] =
    split < 0 ? [] : [text.slice(split + NOTE_SEPARATOR.length)];
  if (q.kind === "text") {
    value.answers = [text];
    return value;
  }
  head.split(ANSWER_SEPARATOR).forEach((part) => {
    const option = matchOption(q, part);
    if (option) value.answers.push(option);
    else if (
      q.kind === "number" &&
      !value.answers.length &&
      /^\d+(\.\d+)?$/.test(part)
    )
      value.answers.push(part);
    else if (
      q.kind === "date" &&
      !value.answers.length &&
      dayjs(part).isValid()
    )
      value.answers.push(part);
    else if (q.kind === "confirm" && part === CONFIRMED)
      value.answers.push(part);
    else if (part) notes.unshift(part);
  });
  value.notes = notes.join(NOTE_SEPARATOR);
  return recoverTrailingOption(q, value);
};

// Rows saved while a label went unmatched carry it at the end of the note; it goes back to the tick.
const recoverTrailingOption = (
  q: FreeTextQuestionDef,
  value: QuestionValue
): QuestionValue => {
  if (value.answers.length || !value.notes) return value;
  const parts = value.notes.split(NOTE_SEPARATOR);
  const option = matchOption(q, parts[parts.length - 1]);
  if (!option) return value;
  return { answers: [option], notes: parts.slice(0, -1).join(NOTE_SEPARATOR) };
};

export const valuesFromRows = (
  def: FreeTextScreenerDef,
  questions: ScreenerQuestion[],
  rows: ScreenerQuestionAnwerResponse[]
): FreeTextValues => {
  const values: FreeTextValues = {};
  questionsFor(def, questions).forEach(({ question, q }) => {
    const row = rows.find((r) => r.question === question.question);
    const isLegacy = String(row?.answer ?? "") === FREE_TEXT_ANSWER;
    const parsed = fromAnswerString(
      q,
      (isLegacy ? row?.answerString : row?.answer)?.trim() ?? ""
    );
    const note = isLegacy ? "" : (row?.answerString ?? "").trim();
    values[question.uuid!] = recoverTrailingOption(
      q,
      note
        ? {
            ...parsed,
            notes: [note, parsed.notes].filter(Boolean).join(NOTE_SEPARATOR),
          }
        : parsed
    );
  });
  return values;
};

const HOME_HEALTH_TYPES: string[] = Object.values(HOME_HEALTH_SCREENER_TYPES);
const YES_NO_SET = new Set(YES_NO.map((it) => it.toLowerCase()));

const isYesNoOptions = (answerOptions: string[]) =>
  answerOptions.length === YES_NO.length &&
  answerOptions.every((it) => YES_NO_SET.has(it.toLowerCase()));

/** Rows for API questions the definition does not cover, from the API's own answer options: Yes/No, else choose-one. Ids are negative so they never collide with seeded ones. */
const uncoveredQuestions = (
  def: FreeTextScreenerDef | undefined,
  questions: ScreenerQuestion[],
  answerOptions: string[]
): FreeTextQuestionDef[] => {
  const covered = new Set(
    def ? questionsFor(def, questions).map(({ question }) => question.uuid) : []
  );
  const hidden = new Set((def?.hiddenQuestions ?? []).map(normaliseText));
  const kind = isYesNoOptions(answerOptions) ? "yesNo" : "single";
  return questions
    .filter(
      (question) =>
        !covered.has(question.uuid) &&
        !hidden.has(normaliseText(question.question))
    )
    .map((question, index) => ({
      questionId: -(index + 1),
      questionText: question.question,
      kind,
      audience: "PATIENT",
      options: answerOptions,
    }));
};

/** The definition to render: the hand-written one, plus API-driven rows for any Home Health question it does not cover. */
export const resolveScreener = (
  type: string | null | undefined,
  questions: ScreenerQuestion[] | undefined,
  answerOptions: string[] | undefined
): FreeTextScreenerDef | undefined => {
  const def = getFreeTextScreener(type);
  const isHomeHealth = HOME_HEALTH_TYPES.includes(normaliseType(type));
  if (!isHomeHealth || !questions?.length || !answerOptions?.length) return def;
  const extra = uncoveredQuestions(def, questions, answerOptions);
  if (!extra.length) return def;
  return { ...def, questions: [...(def?.questions ?? []), ...extra] };
};
